import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Notification from './Notification';
import { getBulkPayeeJobStatus, sendEmailNotification } from '../services';
import { setGlobalNotifyFn } from './GlobalNotificationManager';

const GlobalNotificationContext = createContext();

export const useGlobalNotification = () => useContext(GlobalNotificationContext);

export const GlobalNotificationProvider = ({ children }) => {
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationType, setNotificationType] = useState('info');
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [jobInitiatedNotification, setJobInitiatedNotification] = useState(false);

    useEffect(() => {
        setGlobalNotifyFn(({ type, message }) => {
            setNotificationType(type);
            setNotificationMessage(message);
            setShowNotification(true);
        });

        return () => setGlobalNotifyFn(null);
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setShowNotification(false), 5000);
        return () => clearTimeout(timeout);
    }, [showNotification]);

    // Polling logic
    useEffect(() => {
        const storedJobId = localStorage.getItem('bulkPayeeApprovalJobId');
        setJobId(storedJobId);
        let interval;
        async function pollJobStatus() {
            if (storedJobId) {
                try {
                    const res = await getBulkPayeeJobStatus(storedJobId);
                    setJobStatus(res.data.status);
                    if (res.data.status === 'active' && !jobInitiatedNotification) {
                        setShowNotification(true);
                        setNotificationMessage(`Bulk approval for ${res.data.batchName} batch is processing...`);
                        setNotificationType('info');
                        setJobInitiatedNotification(true);
                    } else if (
                        res.data.status === 'completed' ||
                        res.data.status === 'failed' ||
                        res.data.status === 'error'
                    ) {
                        if(res.data.status === 'completed') {
                            try {
                                await sendEmailNotification(res.data.batchId);
                            } catch (emailError) {
                                console.error('Failed to send email notification:', emailError);
                                // setShowNotification(true);
                                // setNotificationMessage('Error sending email notification to commission analyst');
                                // setNotificationType('error');
                            }
                        }
                        setShowNotification(true);
                        setNotificationMessage(
                            res.data.status === 'completed'
                                ? `Bulk approval for ${res.data.batchName} batch is completed. Please refresh the page.`
                                : res.data.status === 'failed'
                                ? `Bulk approval for ${res.data.batchName} batch is failed`
                                : res.data.status === 'active'
                                ? `Bulk approval for ${res.data.batchName} batch is processing...`
                                : 'Error checking job status.'
                        );
                        setNotificationType(res.data.status === 'completed' ? 'success' : res.data.status === 'active' ? 'info' : 'error');
                        clearInterval(interval);
                    }
                } catch {
                    setJobStatus('error');
                    setShowNotification(true);
                    setNotificationMessage('Error checking job status.');
                    setNotificationType('error');
                    clearInterval(interval);
                }
            }
        }

        if (storedJobId) {
            pollJobStatus(); // Poll instantly on load
            interval = setInterval(pollJobStatus, 1000 * 60 * 2); // Then every 10 sec
        }

        return () => clearInterval(interval);
    }, [jobId, jobInitiatedNotification]);

    const show = useCallback((message, type = 'success', job = null) => {
        setNotificationMessage(message);
        setNotificationType(type);
        setShowNotification(true);
        setJobId(job);
        if (job) {
            localStorage.setItem('bulkPayeeApprovalJobId', job);
        }
        setJobInitiatedNotification(false);
    }, [jobStatus]);

    const hide = useCallback(() => {
        setShowNotification(false);
        setNotificationMessage('');
        if(jobStatus === 'completed' || jobStatus === 'failed' || jobStatus === 'error') {
            setJobId(null);
            setJobStatus(null);
            localStorage.removeItem('bulkPayeeApprovalJobId');
        }
        setJobInitiatedNotification(true);
    }, [jobStatus]);

    return (
        <GlobalNotificationContext.Provider value={{ show, hide }}>
            {children}
            {!!showNotification && notificationMessage !== '' && (
                <Notification
                    showNotification={showNotification}
                    notificationMessage={notificationMessage}
                    notificationType={notificationType}
                    jobId={jobId}
                    onClose={hide}
                />
            )}
        </GlobalNotificationContext.Provider>
    );
};